/**
 * =============================================================================
 * SPEQ55 RFP Pro - Usage Management Admin Page
 * =============================================================================
 *
 * Admin page for viewing and managing user usage limits.
 * - super_admin and admin_sales can view all users and modify limits
 * - org_admin can view users in their organization (read-only)
 *
 * @module pages/admin/UsageManagement
 * @author SPEQ55
 */

import { useState, useEffect, useCallback } from 'react';
import { getAllUserUsage, setUserLimit, removeUserLimit } from '../../api/usageApi';
import { useAuth } from '../../contexts/AuthContext';

function UsageManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [overLimitOnly, setOverLimitOnly] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Aggregate stats (platform-wide summary)
  const [aggregateStats, setAggregateStats] = useState(null);

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Form state
  const [customLimit, setCustomLimit] = useState(20);
  const [limitEnabled, setLimitEnabled] = useState(true);
  const [overrideReason, setOverrideReason] = useState('');

  // Check if user can modify limits
  const canModifyLimits = user?.role === 'super_admin' || user?.role === 'admin_sales';

  /**
   * Load platform-wide aggregate stats.
   * Separated from paginated data so it only runs on mount and after mutations.
   */
  const loadAggregateStats = useCallback(async () => {
    try {
      const allData = await getAllUserUsage({ limit: 10000, offset: 0 });
      const allUsers = allData.users || [];
      const totalAnalyses = allUsers.reduce((sum, u) => sum + (u.count || 0), 0);
      const totalLimit = allUsers.reduce((sum, u) => sum + (u.limit || 0), 0);
      const usersOverLimit = allUsers.filter((u) => !u.canCreate).length;
      const usersNearLimit = allUsers.filter((u) => u.canCreate && u.percentUsed >= 90).length;
      setAggregateStats({
        totalUsers: allUsers.length,
        totalAnalyses,
        totalLimit,
        platformPercentUsed: totalLimit > 0 ? Math.round((totalAnalyses / totalLimit) * 100) : 0,
        usersOverLimit,
        usersNearLimit,
      });
    } catch {
      // Silently fail — aggregate stats are supplementary and should not
      // block the main user table from rendering.
    }
  }, []);

  /**
   * Load users with current filters.
   */
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        overLimitOnly,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };

      const data = await getAllUserUsage(filters);
      setUsers(data.users);
      setTotal(data.total);
    } catch {
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [overLimitOnly, currentPage, itemsPerPage]);

  // Load paginated data on filter/page changes
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load aggregate stats only on mount
  useEffect(() => {
    loadAggregateStats();
  }, [loadAggregateStats]);

  /**
   * Filter users by search term.
   */
  const filteredUsers = users.filter((u) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search);
  });

  /**
   * Open modal for a user.
   */
  const openModal = (user) => {
    setSelectedUser(user);
    setCustomLimit(user.limit);
    setLimitEnabled(user.canCreate !== false);
    setOverrideReason('');
    setModalError(null);
    setShowModal(true);
  };

  /**
   * Close modal.
   */
  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setModalError(null);
  };

  /**
   * Handle setting custom limit.
   */
  const handleSetLimit = async (e) => {
    e.preventDefault();

    if (!overrideReason.trim()) {
      setModalError('Override reason is required');
      return;
    }

    setModalLoading(true);
    setModalError(null);

    try {
      await setUserLimit(selectedUser.userId, {
        monthlyAnalysisLimit: customLimit,
        limitEnabled,
        overrideReason: overrideReason.trim(),
      });

      // Reload users and aggregate stats
      await loadUsers();
      await loadAggregateStats();
      closeModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update limit');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Handle removing custom limit.
   */
  const handleRemoveLimit = async () => {
    if (!confirm('Remove custom limit and revert to default (20/month)?')) {
      return;
    }

    setModalLoading(true);
    setModalError(null);

    try {
      await removeUserLimit(selectedUser.userId);

      // Reload users and aggregate stats
      await loadUsers();
      await loadAggregateStats();
      closeModal();
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to remove limit');
    } finally {
      setModalLoading(false);
    }
  };

  /**
   * Get status badge color.
   */
  const getStatusBadge = (user) => {
    if (!user.canCreate) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Over Limit
        </span>
      );
    }
    if (user.percentUsed >= 90) {
      return (
        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
          Near Limit
        </span>
      );
    }
    return (
      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
        OK
      </span>
    );
  };

  /**
   * Calculate total pages.
   */
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-accent-800">Usage Management</h1>
        <p className="mt-1 text-accent-500">
          View and manage monthly analysis usage limits for users
        </p>
      </div>

      {/* Platform-Wide Usage Summary */}
      {aggregateStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="text-sm font-medium text-accent-500">Total Users</div>
            <div className="mt-1 text-2xl font-bold text-accent-800">
              {aggregateStats.totalUsers}
            </div>
          </div>
          <div className="card p-4">
            <div className="text-sm font-medium text-accent-500">Total Analyses (This Period)</div>
            <div className="mt-1 text-2xl font-bold text-accent-800">
              {aggregateStats.totalAnalyses}
              <span className="text-sm font-normal text-accent-400">
                {' '}
                / {aggregateStats.totalLimit}
              </span>
            </div>
            <div className="text-xs text-accent-500 mt-1">
              {aggregateStats.platformPercentUsed}% of total capacity
            </div>
          </div>
          <div className="card p-4">
            <div className="text-sm font-medium text-accent-500">Users Near Limit</div>
            <div className="mt-1 text-2xl font-bold text-yellow-600">
              {aggregateStats.usersNearLimit}
            </div>
            <div className="text-xs text-accent-500 mt-1">90%+ usage</div>
          </div>
          <div className="card p-4">
            <div className="text-sm font-medium text-accent-500">Users Over Limit</div>
            <div className="mt-1 text-2xl font-bold text-red-600">
              {aggregateStats.usersOverLimit}
            </div>
            <div className="text-xs text-accent-500 mt-1">Cannot create analyses</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-accent-700 mb-1">Search Users</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full px-3 py-2 border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {/* Filter */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overLimitOnly}
                  onChange={(e) => {
                    setOverLimitOnly(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="w-4 h-4 text-primary-600 border-accent-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-accent-700">
                  Show only users over limit
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-accent-500">No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-accent-50 border-b border-accent-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-700 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-700 uppercase tracking-wider">
                      Usage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-700 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-accent-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-accent-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.userId} className="hover:bg-accent-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-accent-900">{user.name}</div>
                          <div className="text-sm text-accent-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-accent-900">
                          {user.count} / {user.limit}
                        </div>
                        <div className="text-xs text-accent-500">{user.percentUsed}% used</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(user)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => openModal(user)}
                          className="text-primary-600 hover:text-primary-900 font-medium"
                        >
                          {canModifyLimits ? 'Manage' : 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-accent-600">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, total)} of {total} users
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-accent-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-accent-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* User Detail Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-accent-900">
                  {canModifyLimits ? 'Manage Usage Limit' : 'View Usage'}
                </h2>
                <button onClick={closeModal} className="text-accent-400 hover:text-accent-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              <div className="bg-accent-50 rounded-lg p-4 mb-6">
                <div className="text-sm font-medium text-accent-700 mb-1">User</div>
                <div className="text-accent-900 font-medium">{selectedUser.name}</div>
                <div className="text-sm text-accent-500">{selectedUser.email}</div>
              </div>

              {/* Current Usage */}
              <div className="bg-accent-50 rounded-lg p-4 mb-6">
                <div className="text-sm font-medium text-accent-700 mb-2">Current Usage</div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-accent-900">Analyses this month:</span>
                  <span className="font-bold text-accent-900">
                    {selectedUser.count} / {selectedUser.limit}
                  </span>
                </div>
                <div className="w-full bg-accent-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      selectedUser.percentUsed >= 90
                        ? 'bg-red-500'
                        : selectedUser.percentUsed >= 70
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                    }`}
                    style={{
                      width: `${Math.min(selectedUser.percentUsed, 100)}%`,
                    }}
                  />
                </div>
                <div className="text-xs text-accent-500 mt-1">{selectedUser.percentUsed}% used</div>
              </div>

              {/* Error Alert */}
              {modalError && (
                <div className="bg-error-light border-l-4 border-error p-3 rounded mb-4">
                  <p className="text-sm text-error-dark">{modalError}</p>
                </div>
              )}

              {/* Modify Limit Form (admin only) */}
              {canModifyLimits ? (
                <form onSubmit={handleSetLimit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-accent-700 mb-1">
                      Monthly Analysis Limit
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={customLimit}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomLimit(val === '' ? '' : parseInt(val, 10) || 0);
                      }}
                      className="w-full px-3 py-2 border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={limitEnabled}
                        onChange={(e) => setLimitEnabled(e.target.checked)}
                        className="w-4 h-4 text-primary-600 border-accent-300 rounded focus:ring-primary-500"
                      />
                      <span className="text-sm font-medium text-accent-700">
                        Enforce usage limits for this user
                      </span>
                    </label>
                    <p className="text-xs text-accent-500 mt-1 ml-6">
                      Uncheck to allow unlimited analyses
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-accent-700 mb-1">
                      Reason for Override <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      rows={3}
                      maxLength={1000}
                      className="w-full px-3 py-2 border border-accent-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Explain why you're changing this user's limit..."
                      required
                    />
                    <p className="text-xs text-accent-500 mt-1">
                      {overrideReason.length} / 1000 characters
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button type="submit" disabled={modalLoading} className="flex-1 btn-primary">
                      {modalLoading ? 'Updating...' : 'Update Limit'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRemoveLimit}
                      disabled={modalLoading}
                      className="flex-1 btn-outline"
                    >
                      Revert to Default
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-accent-600">
                    You can view usage statistics but cannot modify limits.
                  </p>
                  <button onClick={closeModal} className="mt-4 btn-primary">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UsageManagement;
